import React, { useState } from 'react'
import PropTypes from 'prop-types'

/**
 * Conditionally loaded PropViewer component for development debugging.
 * Do not import directly. See './index.js' for Barrel Import pattern details.
 *
 * @example Use in any react component:
 * import { PropViewer } from 'components/Utilities'
 */
const PropViewer = ({ props, label = 'Props' }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [collapsedPaths, setCollapsedPaths] = useState(new Set(['root'])) // Start with root collapsed

  const toggleCollapsed = (path) => {
    const newCollapsed = new Set(collapsedPaths)
    if(newCollapsed.has(path)) {
      newCollapsed.delete(path)
    } else {
      newCollapsed.add(path)
    }
    setCollapsedPaths(newCollapsed)
  }

  const renderValue = (value, depth = 0, path = '') => {
    if(value === null) return <span style={{ color: '#999' }}>null</span>
    if(value === undefined) return <span style={{ color: '#999' }}>undefined</span>
    if(typeof value === 'string') return <span style={{ color: '#006400' }}>&quot;{value}&ldquo;</span>
    if(typeof value === 'number') return <span style={{ color: '#0066cc' }}>{value}</span>
    if(typeof value === 'boolean') return <span style={{ color: '#cc6600' }}>{value.toString()}</span>
    if(typeof value === 'function') return <span style={{ color: '#666' }}>[Function]</span>

    if(Array.isArray(value)) {
      if(value.length === 0) return <span style={{ color: '#666' }}>[]</span>

      const isCollapsed = collapsedPaths.has(path)
      const previewText = `Array(${value.length})`

      return (
        <div>
          <span
            onClick={() => toggleCollapsed(path)}
            style={{
              cursor:     'pointer',
              userSelect: 'none',
              color:      '#0066cc',
              fontWeight: '500'
            }}
          >
            <span style={{ color: '#666', marginRight: '4px' }}>
              {isCollapsed ? '▶' : '▼'}
            </span>
            [{isCollapsed ? ` ${previewText} ` : ''}
          </span>
          {!isCollapsed && (
            <div style={{ borderLeft: '2px solid #e9ecef', marginLeft: '8px', paddingLeft: '8px' }}>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: '2px' }}>
                  <span style={{ color: '#6c757d', fontSize: '11px' }}>{index}:</span> {renderValue(item, depth + 1, `${path}[${index}]`)}
                  {index < value.length - 1 && <span style={{ color: '#adb5bd' }}>,</span>}
                </div>
              ))}
              <span style={{ color: '#666' }}>]</span>
            </div>
          )}
          {isCollapsed && <span style={{ color: '#666' }}>]</span>}
        </div>
      )
    }

    if(typeof value === 'object') {
      const keys = Object.keys(value)
      if(keys.length === 0) return <span style={{ color: '#666' }}>{'{}'}</span>

      const isCollapsed = collapsedPaths.has(path)
      const previewText = `${keys.length} key${keys.length !== 1 ? 's' : ''}`

      return (
        <div>
          <span
            onClick={() => toggleCollapsed(path)}
            style={{
              cursor:     'pointer',
              userSelect: 'none',
              color:      '#dc3545',
              fontWeight: '500'
            }}
          >
            <span style={{ color: '#666', marginRight: '4px' }}>
              {isCollapsed ? '▶' : '▼'}
            </span>
            {'{'}
            {isCollapsed ? ` ${previewText} ` : ''}
          </span>
          {!isCollapsed && (
            <div style={{ borderLeft: '2px solid #e9ecef', marginLeft: '8px', paddingLeft: '8px' }}>
              {keys.map(key => (
                <div key={key} style={{ marginBottom: '2px' }}>
                  <span style={{ color: '#8b0000', fontWeight: '600' }}>{key}</span>
                  <span style={{ color: '#495057' }}>: </span>
                  {renderValue(value[key], depth + 1, `${path}.${key}`)}
                </div>
              ))}
              <span style={{ color: '#666' }}>{'}'}</span>
            </div>
          )}
          {isCollapsed && <span style={{ color: '#666' }}>{'}'}</span>}
        </div>
      )
    }

    return <span>{String(value)}</span>
  }

  return (
    <div style={{
      position:        'fixed',
      top:             '10px',
      right:           '10px',
      zIndex:          9999,
      backgroundColor: 'white',
      border:          '2px solid #dee2e6',
      borderRadius:    '6px',
      boxShadow:       '0 2px 8px rgba(0,0,0,0.1)',
      fontSize:        '12px',
      fontFamily:      'monospace'
    }}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          backgroundColor: '#f8f9fa',
          border:          'none',
          borderRadius:    '4px',
          padding:         '6px 10px',
          fontSize:        '12px',
          cursor:          'pointer',
          fontWeight:      '600',
          color:           '#495057',
          transition:      'background-color 0.15s ease'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
      >
        {isExpanded ? '▼' : '▶'} {label}
      </button>

      {isExpanded && (
        <div style={{
          marginTop:       '4px',
          padding:         '12px',
          backgroundColor: '#f8f9fa',
          borderTop:       '1px solid #dee2e6',
          borderRadius:    '0 0 4px 4px',
          maxHeight:       '400px',
          maxWidth:        '500px',
          overflow:        'auto',
          lineHeight:      '1.5'
        }}>
          {renderValue(props, 0, 'root')}
        </div>
      )}
    </div>
  )
}

PropViewer.propTypes = {
  props: PropTypes.object.isRequired,
  label: PropTypes.string
}

export default PropViewer